import { SkyAccordion, SkyAccordionItem, SkyInput, SkyText } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import type { NotifySubject } from "@shared/types/notifications";
import {
  cellKey,
  openSubjects,
  repeatWindowProblem,
  shownSubjects,
  subjectSummary,
  subjectsNeedingFix,
  type RuleDraft,
  type RulesDraft,
} from "@/lib/notifications/rulesDraft";
import { useMonitorEnabled } from "@/hooks/use-monitor";
import { FORM_MAX_WIDTH } from "@/lib/layout";
import {
  NotificationSubjectHeading,
  NotificationSubjectSection,
} from "./NotificationSubjectSection";
import { useHeldAddresses, useRecipientPolicy } from "@/lib/notifications/RecipientPolicyContext";

/**
 * Every rule and the repeat window (contract-notif R4, R6, R10): one "What" section per subject
 * (Credentials since 2026-09-28; Data health, skyMonitor, while `features.flags.monitor` is
 * on), three "When" rules each, then "Repeat alerts". The page owns the draft and the save; this
 * form only renders it and reports each edit. A hidden section's rules still travel in the save
 * unchanged, because the rules PUT carries every cell.
 *
 * Round 3 (owner, 2026-09-30: "Too many boxes ... collapse what imports, what transformations"):
 * the subjects are one collapsible list (`SkyAccordion`), each header naming the subject, "{n} of
 * {m} on" and its status in words and colour. A section starts open only when it has a rule on (a
 * new instance opens Imports); after that the reader opens and closes them, and a copy opens every
 * section it wrote to. A closed section's rules are not in the page, and still in the draft, so a
 * header says "Needs a fix" while one of its rules does, and a refused Save opens that section.
 */
export function NotificationRulesForm({
  draft,
  readOnly,
  hasWebhook,
  emailsMasked,
  fieldErrors,
  copyStatus,
  revealErrors = 0,
  initialOpen,
  onRuleChange,
  onCopy,
  onRepeatWindowChange,
}: {
  draft: RulesDraft;
  readOnly: boolean;
  /** The instance has a Teams webhook, so a Teams switch reaches somebody. */
  hasWebhook: boolean;
  emailsMasked: boolean;
  fieldErrors: Record<string, string>;
  copyStatus: { cell: string; text: string } | null;
  /** Bumped by the page on each refused Save: every section with a rule to fix opens. */
  revealErrors?: number;
  /**
   * A subject a link asked to open (`?open=data_health` from skyMonitor, round 3), open at mount
   * beside the ones with a rule on, and once it is shown if its flag arrives later.
   */
  initialOpen?: NotifySubject;
  onRuleChange: (next: RuleDraft) => void;
  onCopy: (rule: RuleDraft) => void;
  onRepeatWindowChange: (value: string) => void;
}) {
  const repeatProblem = readOnly ? null : repeatWindowProblem(draft.repeatWindow);
  const monitor = useMonitorEnabled();
  const subjects = shownSubjects(monitor);
  const [open, setOpen] = useState<NotifySubject[]>(() => {
    const opened = openSubjects(draft, subjects);
    return initialOpen && subjects.includes(initialOpen) && !opened.includes(initialOpen)
      ? [...opened, initialOpen]
      : opened;
  });
  const policy = useRecipientPolicy();
  const held = useHeldAddresses();
  const needFix = subjectsNeedingFix(draft, subjects, fieldErrors, readOnly, policy, held);
  // Walk 2026-10-02, D14: the copy button's sentence is on screen once, under the first rule that
  // is on in a section that is open (the others carry it for a screen reader only).
  const firstOn = subjects
    .filter((subject) => open.includes(subject))
    .flatMap((subject) => draft.rules.filter((rule) => rule.subject === subject))
    .find((rule) => rule.enabled);
  const copyHelpCell = firstOn ? cellKey(firstOn.subject, firstOn.outcome) : null;

  // Adjusted during render, not in an effect (React's "storing information from previous
  // renders"): each refused Save bumps `revealErrors`, and every section with a rule to fix opens,
  // so the bar's "They are marked above" is true of a section the reader had closed (review S2).
  const [seenReveal, setSeenReveal] = useState(revealErrors);
  if (revealErrors !== seenReveal) {
    setSeenReveal(revealErrors);
    if (needFix.length > 0) setOpen((current) => [...new Set([...current, ...needFix])]);
  }
  // The skyMonitor flag can arrive after the form: Data health then opens as it would have at
  // mount, when it has a rule on.
  const [seenMonitor, setSeenMonitor] = useState(monitor);
  if (monitor !== seenMonitor) {
    setSeenMonitor(monitor);
    const ruleOn = draft.rules.some((rule) => rule.subject === "data_health" && rule.enabled);
    if (monitor && (ruleOn || initialOpen === "data_health")) {
      setOpen((current) => [...new Set<NotifySubject>([...current, "data_health"])]);
    }
  }

  return (
    <div className={`flex flex-col gap-4 ${FORM_MAX_WIDTH}`} data-testid="notification-rules-form">
      {/* The subject headers are the accordion's h3s (its Radix header), so they sit under an h2 of
          their own: a page heading may only step down one level (axe heading-order). */}
      <section aria-labelledby="notification-rules-heading" className="flex flex-col gap-2">
        <SkyText as="h2" size={400} weight="semibold" block id="notification-rules-heading">
          Notification rules
        </SkyText>
        <SkyAccordion
          multiple
          openItems={open}
          onToggle={(_value, items) => setOpen(items as NotifySubject[])}
          className="rounded-lg border border-border bg-card px-2"
          testId="notification-subjects"
        >
          {subjects.map((subject) => (
            <SkyAccordionItem
              key={subject}
              value={subject}
              heading={
                <NotificationSubjectHeading
                  subject={subject}
                  summary={subjectSummary(draft, subject, hasWebhook)}
                  needsFix={needFix.includes(subject)}
                />
              }
              panelClassName="pr-2"
            >
              <NotificationSubjectSection
                subject={subject}
                rules={draft.rules.filter((rule) => rule.subject === subject)}
                readOnly={readOnly}
                emailsMasked={emailsMasked}
                fieldErrors={fieldErrors}
                copyStatus={copyStatus}
                copyHelpCell={copyHelpCell}
                hasWebhook={hasWebhook}
                onRuleChange={onRuleChange}
                onCopy={(rule) => {
                  onCopy(rule);
                  // The copy wrote the same "When" of every subject shown: open them all to show it.
                  setOpen((current) => [...new Set([...current, ...subjects])]);
                }}
              />
            </SkyAccordionItem>
          ))}
        </SkyAccordion>
      </section>

      <section
        aria-labelledby="repeat-alerts-heading"
        className="rounded-lg border border-border bg-card p-4"
      >
        <SkyText as="h2" size={400} weight="semibold" block id="repeat-alerts-heading">
          Repeat alerts
        </SkyText>
        {readOnly ? (
          <SkyText size={300} block testId="repeat-window">
            Send a repeat alert for the same schedule or connection at most every{" "}
            {draft.repeatWindow} minutes.
          </SkyText>
        ) : (
          <SkyInput
            label="Send a repeat alert for the same schedule or connection at most every"
            type="number"
            value={draft.repeatWindow}
            suffix="minutes"
            hint="0 sends every alert. Up to 1440 (one day)."
            validationState={repeatProblem ? "error" : "none"}
            validationMessage={repeatProblem ?? undefined}
            testId="repeat-window"
            onChange={(event) => onRepeatWindowChange(event.target.value)}
          />
        )}
      </section>
    </div>
  );
}
